import GroupLayer from "@arcgis/core/layers/GroupLayer";
import Layer from "@arcgis/core/layers/Layer";
import TileLayer from "@arcgis/core/layers/TileLayer";
import WMSLayer from "@arcgis/core/layers/WMSLayer";
import CustomContent from "@arcgis/core/popup/content/CustomContent";
import PopupTemplate from "@arcgis/core/PopupTemplate";

import {
  applyCurrentFiltersOnLayer,
  composeIsoAndIsDeletedWhereCondition,
} from "../components/Filter/ApplyFilters";
import roads from "../data/roads.json";
import {
  addOperationLayer,
  updateOperationLayer,
} from "../redux/action/MapView-action";
import { LAYER_STATES } from "../redux/reducers/mapViewReducer";
import store from "../redux/store";
import {
  getLayerFromId,
  getLayerInformationFromAlias,
  getLayerTitleFromAlias,
  openDefaultPopup,
  view,
} from "../utils/API";
import { setLayerClustering } from "../utils/clustering";
import { extend, getConfigISO, ROLE_EDITOR } from "../utils/helper";
import { addSymbology } from "../utils/symbologies";
import { createCustomPopup } from "./custom-popup-content";
import { CommentBoxes } from "./graphicLayerUtils/CommentBoxes";
import { Conops } from "./graphicLayerUtils/Conops";
import { Epam } from "./graphicLayerUtils/Epam";

const layerSequence = ["polygon", "polyline", "line", "point"];

export const createLayer = (
  layerAlias,
  index,
  config,
  t,
  i18n,
  activeModule,
  openSnackbar
) => {
  //Get layer definition, if not found, just create a default one
  let layerConfig = config.layerConfig[layerAlias];
  try {
    const enterpriseUser = store.getState().userReducer?.enterpriseUser;
    const arcGisUser = store.getState().userReducer?.arcGisUser;
    if ((enterpriseUser || arcGisUser) && layerConfig && layerConfig.wfpId) {
      layerConfig.id = layerConfig.wfpId;
      if (layerConfig.wfpInstance) {
        layerConfig.instance = layerConfig.wfpInstance;
      }
    }
  } catch (err) {
    console.log(err);
  }

  if (!layerConfig) {
    layerConfig = { ...config.layerDefaults };
    layerConfig.id = layerAlias;
    config.layerConfig[layerAlias] = layerConfig;
  }
  layerConfig.mapReloadCount = store.getState().mapViewReducer.mapReloadCount;
  layerConfig.alias = layerAlias;
  layerConfig.layerModules = [];
  let modules = config.modules;
  modules &&
    Object.keys(modules).forEach((moduleName) => {
      const currModule = modules[moduleName];
      if (config.activeModules && config.activeModules.includes(moduleName)) {
        if (Array.isArray(currModule.layers)) {
          currModule.layers.forEach((moduleLayer) => {
            if (moduleLayer === layerAlias)
              layerConfig.layerModules.push(moduleName);
          });
        }

        if (Array.isArray(currModule?.optionalLayers)) {
          currModule.optionalLayers.forEach((moduleLayer) => {
            if (moduleLayer === layerAlias) {
              layerConfig.layerModules.push(moduleName);
              layerConfig.isOptional = true;
            }
          });
        }
      }
    });

  if (layerConfig.id) {
    const operationLayerObject = {
      id: layerConfig.id,
      alias: layerAlias,
      title: t(getLayerTitleFromAlias(layerAlias, config), layerAlias),
      information: t(getLayerInformationFromAlias(layerAlias, config), ""),
      state: LAYER_STATES.LOADING,
      layerConfig: layerConfig,
      getLayerTitle: (t) => {
        return t(getLayerTitleFromAlias(layerAlias, config), layerAlias);
      },
      getLayerInformation: (t) => {
        return t(getLayerInformationFromAlias(layerAlias, config), "");
      },
    };
    store.dispatch(addOperationLayer(operationLayerObject));
    let layerId = layerConfig.id;
    const enterpriseUser = store.getState().userReducer?.enterpriseUser;

    //Load normal layers with id
    let layerPromise = getLayerFromId(Layer, layerId, layerConfig.instance);
    return layerPromise
      .then((layerPreLoad) => {
        //For conops layers it is important to load all fields (otherwise the layerview query will not work
        if (
          layerConfig.isConops ||
          layerConfig.commentBoxOps ||
          layerConfig.isEpam
        ) {
          layerPreLoad.outFields = ["*"];
        }

        if (layerConfig?.wfpId && !enterpriseUser) {
          return layerPreLoad;
        }

        return layerPreLoad.load().then((layer) => {
          layer.layerConfig = layerConfig;
          layer.mode = "on-the-fly";
          setCustomLayerAttributes(
            layer,
            index,
            layerConfig.layerModules,
            layerId
          );
          setLayerVisibility(layer, config, layerConfig);
          checkAndAddLayerToMap(
            layer,
            config,
            layerConfig,
            t,
            i18n,
            activeModule,
            openSnackbar
          );
          return layer;
        });

        //return layerPromise
      })
      .catch((err) => {
        store.dispatch(
          updateOperationLayer({
            id: layerId,
            values: {
              state: LAYER_STATES.ERROR,
              layerConfig,
            },
          })
        );
        console.log(err);

        return null;
      });
  } else if (layerConfig.subLayers) {
    //Load group layers
    let layerPromises = [];
    Object.keys(layerConfig.subLayers).forEach((subLayerAlias) => {
      let subLayerConfig = layerConfig.subLayers[subLayerAlias];
      let subLayerPromise = getLayerFromId(Layer, subLayerConfig.id);
      subLayerPromise.then((subLayer) => {
        setCustomLayerAttributes(
          subLayer,
          index,
          layerConfig.layerModules,
          layerAlias,
          subLayerAlias
        );
      });
      layerPromises.push(subLayerPromise);
    });
    return Promise.all(layerPromises).then((resolvedLayers) => {
      let grLayer = new GroupLayer({
        title: layerAlias,
        layers: resolvedLayers,
      });

      setCustomLayerAttributes(
        grLayer,
        index,
        layerConfig.layerModules,
        layerAlias
      );
      setLayerVisibility(grLayer, config, layerConfig);
      return checkAndAddLayerToMap(
        grLayer,
        config,
        layerConfig,
        t,
        i18n,
        activeModule,
        openSnackbar
      );
    });
  } else if (layerAlias.includes("roads_baseline")) {
    const iso3 = getConfigISO(config);
    const configRoads = roads.filter((item) => iso3.includes(item.iso3));

    const layerPromises = [];
    const operationalLayers = [];

    configRoads.map((item) => {
      if (!Array.isArray(item.layers)) {
        return;
      }

      item.layers?.map((item) => {
        const layerId =
          config.role === ROLE_EDITOR ? item.editable : item.viewer;
        if (config.role === ROLE_EDITOR) {
          layerConfig.id = item.editable;
          layerConfig.editable = item.editable;
          layerConfig.viewer = item.viewer;
        } else {
          layerConfig.id = item.viewer;
          layerConfig.editable = item.editable;
        }

        const layerPromise = getLayerFromId(Layer, layerId, item.instance);
        const operationLayerObject = {
          id: layerConfig.id,
          alias: layerAlias,
          title: t(getLayerTitleFromAlias(layerAlias, config), layerAlias),
          information: t(getLayerInformationFromAlias(layerAlias, config), ""),
          state: LAYER_STATES.LOADING,
          layerConfig: layerConfig,
          getLayerTitle: (t) => {
            return t(getLayerTitleFromAlias(layerAlias, config), layerAlias);
          },
          getLayerInformation: (t) => {
            return t(getLayerInformationFromAlias(layerAlias, config), "");
          },
        };
        operationalLayers.push(operationLayerObject);
        store.dispatch(addOperationLayer(operationLayerObject));
        layerPromises.push(layerPromise);
      });
    });
    return Promise.allSettled(layerPromises)
      .then(async (resolvedLayers) => {
        return await Promise.all(
          resolvedLayers.map(async ({ status, value: layerPreLoad }, i) => {
            if (status === "rejected") {
              const rejectedLayer = operationalLayers[i];
              store.dispatch(
                updateOperationLayer({
                  id: rejectedLayer.id,
                  values: {
                    state: LAYER_STATES.ERROR,
                  },
                })
              );

              return null;
            }

            // For conops layers it is important to load all fields (otherwise the layerview query will not work)
            if (layerConfig.isConops || layerConfig.commentBoxOps) {
              layerPreLoad.outFields = ["*"];
            }

            try {
              const layer = await layerPreLoad.load();
              layer.layerConfig = layerConfig;

              setCustomLayerAttributes(
                layer,
                index,
                layerConfig.layerModules,
                layerConfig.id
              );
              setLayerVisibility(layer, config, layerConfig);
              checkAndAddLayerToMap(
                layer,
                config,
                layerConfig,
                t,
                i18n,
                activeModule,
                openSnackbar
              );

              return layer;
            } catch (error) {
              console.error("Error loading layer:", error);
              return null;
            }
          })
        );
      })
      .catch((err) => {
        console.log(err);

        return null;
      });
  } else {
    //Maybe this layer is available only for editors
    return null;
  }
};

const setCustomLayerAttributes = (
  layer,
  index,
  layerModules,
  layerId,
  subLayerId
) => {
  layer.layerIndex = index;
  layer.originalId = layerId;
  layer.layerModules = layerModules;
  layer.originalSubLayerId = subLayerId;
};

export const setLayerVisibility = (layer, config, layerConfig) => {
  let activeModule;
  try {
    activeModule = store.getState().activeModule;
  } catch (err) {
    console.error("Error getting active module: ", err);
  }
  if (layerConfig.layerModules.length > 0) {
    let module = activeModule || config.defaultModule || config.modules[0];

    if (
      config.modules[module]?.optionalLayers?.includes(layerConfig.alias) &&
      !config.modules[module]?.layers.includes(layerConfig.alias)
    ) {
      layer.visible = false;
    } else {
      layer.visible = layerConfig.layerModules.includes(module);
    }
  }
};

const setLayerFilterAndPopup = (layer, config, layerConfig, t, i18n) => {
  layer.layerConfig = layerConfig;
  if (
    !layerConfig.clickable ||
    (layerConfig.onclick && layerConfig.target === "openLinkOnSamePage")
  ) {
    layer.popupEnabled = false;
    layer.popupTemplate = null;
  } else {
    layer.popupTemplate = createCustomPopup(
      config,
      layer,
      PopupTemplate,
      CustomContent,
      t,
      i18n
    );
  }

  if (layer.fields) {
    layer.layerConfig.originalDefinitionExpression = layer.definitionExpression;
    layer.definitionExpression = composeIsoAndIsDeletedWhereCondition(
      layer,
      config
    );
    layer.isFiltered = true;
  }
};
const checkAndAddLayerToMap = (
  layer,
  config,
  layerConfig,
  t,
  i18n,
  activeModule,
  openSnackbar
) => {
  let enterpriseUser = store.getState().userReducer?.enterpriseUser;
  if (layer.type === "feature") {
    if (layer.layerConfig?.wfpId && !enterpriseUser) {
      return;
    }
    setLayerClustering(layer, config, t);
    setLayerFilterAndPopup(layer, config, layerConfig, t, i18n);
    return addLayerToMap(layer, config, null, t, activeModule, openSnackbar);
  } else if (layer.type === "group") {
    configureGroupSubLayers(layer, config, layerConfig, t, i18n);
    return addLayerToMap(layer, config, null, t, activeModule, openSnackbar);
  } else if (layer.type === "map-image") {
    configureImageSubLayers(layer, config, layerConfig, t, i18n);
    return addLayerToMap(layer, config, null, t, activeModule, openSnackbar);
  } else if (layer.type === "wms") {
    if (layerConfig.type === "dcm") {
      const now = new Date();
      const utcHour = now.getUTCHours();

      // Set time parameter based on UTC hour
      let timeParam;
      if (utcHour >= 0 && utcHour < 17) {
        timeParam = "P2D/PRESENT"; // 00:00 - 16:59 UTC
      } else {
        timeParam = "P1D/PRESENT"; // 17:00 - 23:59 UTC
      }

      const wmsLayer = new WMSLayer({
        url: layer.url,
        customParameters: {
          TIME: timeParam,
        },
        sublayers: [
          {
            name: "dcm_prod:dcm_mix_24hr_allscales",
            format: "image/png",
            transparent: false,
          },
        ],
        useViewTime: false,
        visible: layer.visible,
      })
        .load()
        .then((res) => {
          res.layerConfig = layerConfig;
          res.visible = layer.visible;
          res.layerIndex = layer.index;
          res.originalId = layer.layerId;
          res.layerModules = layer.layerModules;
          res.originalSubLayerId = layer.subLayerId;
          return addLayerToMap(
            res,
            config,
            null,
            t,
            activeModule,
            openSnackbar
          );
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      const wmsLayer = new WMSLayer({
        url: layer.url,
      });
      return wmsLayer
        .load()
        .then((wmsLayer) => {
          wmsLayer.sublayers.forEach((sublayer) => {
            sublayer.visible = false;
          });
          return addLayerToMap(
            wmsLayer,
            config,
            null,
            t,
            activeModule,
            openSnackbar
          );
        })
        .catch((err) => {
          console.log(err);
        });
    }
  } else if (layer.type === "tile" && layer.url) {
    let tileLayer = new TileLayer({ url: layer.url });

    setCustomLayerAttributes(
      tileLayer,
      layer.layerIndex,
      layer.layerModules,
      layer.originalId
    );
    setLayerFilterAndPopup(tileLayer, config, layerConfig, t, i18n);
    return addLayerToMap(
      tileLayer,
      config,
      null,
      t,
      activeModule,
      openSnackbar
    );
  } else if (layer.type === "imagery") {
    return addLayerToMap(layer, config, null, t, activeModule, openSnackbar);
  }
};

const configureGroupSubLayers = (parentLayer, config, layerConfig, t, i18n) => {
  parentLayer.layers.map((subLayer) => {
    subLayer.load().then((subLayer) => {
      let subLayerConfig = layerConfig;
      if (layerConfig.subLayers && layerConfig.subLayers[subLayer.layerId])
        subLayerConfig = extend(
          layerConfig,
          layerConfig.subLayers[subLayer.layerId]
        );

      setCustomLayerAttributes(
        subLayer,
        parentLayer.layerIndex,
        parentLayer.layerModules,
        parentLayer.originalId,
        subLayer.layerId
      );
      setLayerFilterAndPopup(subLayer, config, subLayerConfig, t, i18n);
    });
    return subLayer;
  });
};

const configureImageSubLayers = (parentLayer, config, layerConfig, t, i18n) => {
  parentLayer.sublayers.map((subLayer) => {
    //const id = parentLayer.originalId + "." + subLayer.id
    let subLayerConfig = layerConfig;
    if (layerConfig.subLayers && layerConfig.subLayers[subLayer.id])
      subLayerConfig = extend(layerConfig, layerConfig.subLayers[subLayer.id]);

    //Inherit layer configuration from the head
    subLayer.originalId = parentLayer.originalId;
    subLayer.originalSubLayerId = subLayer.id;

    if (subLayer.sublayers)
      configureImageSubLayers(subLayer, config, layerConfig, t, i18n);

    subLayer.createFeatureLayer().then((featureLayer) => {
      if (featureLayer)
        setLayerFilterAndPopup(subLayer, config, subLayerConfig, t, i18n);
    });

    return subLayer;
  });
};

const zoomToCountry = async (config, l, activeModule, openSnackbar, t) => {
  try {
    const zoom = activeModule
      ? config.modules[activeModule].zoom ?? config.zoom
      : config.zoom;

    if (
      l.layerConfig?.id === "d034f64504a04942a87cdd0165242679" &&
      l.layerConfig?.alias === "world_boundaries" &&
      zoom === undefined
    ) {
      const iso3 = getConfigISO(config);
      const queryCountry = iso3[0];
      const query = {
        where: `iso3 = '${queryCountry}'`,
        returnGeometry: true,
      };

      const result = await l.queryFeatures(query);

      if (result.features && result.features.length > 0) {
        const feature = result.features[0];
        // Get the extent of the polygon
        const extent = feature.geometry.extent.expand(1.1); // Expand for better view

        // Zoom to the extent
        view.goTo(
          {
            target: extent,
          },
          {
            duration: 1000, // Animation duration in milliseconds
            easing: "ease-out",
          }
        );
      }
    }
  } catch (err) {
    openSnackbar(t("screen.message.zoomError"));
  }
};
/**
 *
 * Call this whenever a layer should be added.
 * It respects the following layer order:
 * -polygons
 * -lines
 * -points
 * -undefined
 */
export const addLayerToMap = (
  layer,
  config,
  map,
  t,
  activeModule,
  openSnackbar
) => {
  let actualMap = map ? map : view.map;
  let layerIndex = 0;
  const enterpriseUser = store.getState().userReducer?.enterpriseUser;
  if (layer.layerConfig?.wfpId && !enterpriseUser) {
    return;
  }
  layer
    .load()
    .then((l) => {
      let mapReloadCount = 0;
      addSymbology(l, config, t);
      if (layer.layerConfig?.refreshInterval) {
        const interval = layer.layerConfig.refreshInterval;
        if (!isNaN(parseFloat(interval)) && isFinite(interval)) {
          console.log(
            layer.title +
              " was set to be refreshed with the interval: " +
              interval +
              " minute(s)"
          );
          layer.refreshInterval = interval;
        }
      }
      actualMap.layers.forEach((otherLayer) => {
        if (isAbove(config, activeModule, l, otherLayer)) layerIndex++;
      });

      let shouldAddLayer = true;
      try {
        mapReloadCount = store.getState().mapViewReducer.mapReloadCount;
        const layersCurrentReload = l.layerConfig?.mapReloadCount || 0;

        if (mapReloadCount !== layersCurrentReload) {
          shouldAddLayer = false;
        }

        if (layer.layerConfig.wfpId && !enterpriseUser) {
          shouldAddLayer = false;
        }
      } catch (err) {
        console.log(err);
      }

      //Custom layer methods:
      l.getLayerTitle = (t) => {
        let transactionLabel =
          "layer.title." +
          (l.layerConfig?.titleLabel
            ? l.layerConfig.titleLabel
            : l.originalId +
              (l.originalSubLayerId ? "-" + l.originalSubLayerId : "")) +
          ".title";
        return t(transactionLabel, l.title);
      };
      //Custom layer methods:
      l.getLayerInformation = (t) => {
        let transactionLabel =
          "layer.title." +
          (l.layerConfig?.titleLabel
            ? l.layerConfig.titleLabel
            : l.originalId +
              (l.originalSubLayerId ? "-" + l.originalSubLayerId : "")) +
          ".information";
        return t(transactionLabel, l.title);
      };

      if (!shouldAddLayer) return;

      actualMap.add(l, layerIndex);

      addLabelingInfo(l);

      initFilters(layer, config, activeModule);
      prepareLayerForOfflineUse(l);
      zoomToCountry(config, l, activeModule, openSnackbar, t);

      if (layer.layerConfig?.isConops) {
        setTimeout(() => {
          new Conops(actualMap, layer, config, t);
        }, 500);
      }

      if (layer.layerConfig?.commentBoxOps) {
        setTimeout(() => {
          new CommentBoxes(actualMap, layer, config, t);
        }, 500);
      }

      if (layer.layerConfig?.isEpam) {
        setTimeout(() => {
          new Epam(actualMap, layer, config, t);
        }, 500);
      }

      if (l.layerConfig.id) {
        store.dispatch(
          updateOperationLayer({
            id: l.layerConfig.id,
            values: {
              state: LAYER_STATES.LOADED,
              layerConfig: l.layerConfig,
            },
          })
        );
      }

      openDefaultPopup(config, l);
    })
    .catch((err) => {
      console.log(err, layer.title);
    });

  /*
    if (layer.geometryType !== "polyline" && layer.layerConfig && layer.layerConfig.filterExpression){
        view.whenLayerView(layer).then((layerView) => {
            layerView.filter = {
                where: layer.layerConfig.filterExpression
            }
        })
    }
     */
  // addLayerToSearch(layer, view, config, t);
};

// Build a mapping of layer IDs and aliases to their positions for quick lookups
// This avoids repeated linear searches through arrays
const getLayerPositions = (config, moduleId) => {
  if (!config.modules[moduleId]?.layers) return new Map();

  const positions = new Map();
  config.modules[moduleId].layers.forEach((layerId, index) => {
    positions.set(layerId, index);
  });

  const optionalLayerPositions = new Map();
  if (Array.isArray(config.modules[moduleId].optionalLayers)) {
    config.modules[moduleId].optionalLayers.forEach((layerId, index) => {
      optionalLayerPositions.set(layerId, index);
    });
  }

  return [positions, optionalLayerPositions];
};

// Function to get a layer's position (by ID or alias) in a module
const getLayerPosition = (layer, positions) => {
  if (!layer.layerConfig) return -1;

  const posById = positions.get(layer.layerConfig.id);
  if (posById !== undefined) return posById;

  const posByAlias = positions.get(layer.layerConfig.alias);
  if (posByAlias !== undefined) return posByAlias;

  return -1;
};

export const sortActiveModuleLayers = ({
  config,
  activeModule,
  layerA,
  layerB,
}) => {
  if (layerA.type === "graphics") return true;
  if (layerB.type === "graphics") return false;

  if (!layerA.layerConfig) return false;
  if (!layerB.layerConfig) return true;

  const [activePositions, optionalLayerPositions] = getLayerPositions(
    config,
    activeModule
  );
  const posA = getLayerPosition(layerA, activePositions);
  const posB = getLayerPosition(layerB, activePositions);

  // If layerA is optional, put it at the beginning of its geometry type group
  if (layerA.layerConfig?.isOptional || layerB.layerConfig?.isOptional) {
    const lsa = getLayerGeometryType(layerA);
    const lsb = getLayerGeometryType(layerB);
    if (layerA.layerConfig?.isOptional && layerB.layerConfig?.isOptional) {
      const posA = getLayerPosition(layerA, optionalLayerPositions);
      const posB = getLayerPosition(layerB, optionalLayerPositions);
      // If both are in active module, sort by position
      return posA > posB;
    }

    // If geometry types are the same, layerA should be above (at the beginning)
    if (lsa === 0) return false;
    // If layerB is polygon, layerA should be above
    if (lsb === 0) return true;

    if (layerA.layerConfig?.isOptional && layerB.layerConfig?.isOptional) {
      const posA = getLayerPosition(layerA, optionalLayerPositions);
      const posB = getLayerPosition(layerB, optionalLayerPositions);
      console.log(
        `Comparing optional layers: ${layerA.layerConfig.alias} (pos ${posA}) vs ${layerB.layerConfig.alias} (pos ${posB})`
      );

      // If both are optional, sort by position in optional layers
      if (posA >= 0 && posB >= 0) {
        return posA < posB; // Earlier in list = lower in z-index
      }
    }
  }

  // If both are in active module, sort by position
  if (posA >= 0 && posB >= 0) {
    return posA > posB; // Earlier in list = lower in z-index
  }

  // If only one is in active module, prioritize it
  if (posA >= 0) return false; // A comes first in config, so it goes below
  if (posB >= 0) return true; // B comes first in config, so it goes above
};
/**
 * Return true if layerA should be displayed on top of layerB
 */
export const isAbove = (config, activeModule, layerA, layerB) => {
  // Check if either layer is a graphics layer
  if (layerA.type === "graphics") return true;
  if (layerB.type === "graphics") return false;

  if (!layerA.layerConfig) return false;
  if (!layerB.layerConfig) return true;
  // If layerA is optional, put it at the beginning of its geometry type group
  if (layerA.layerConfig?.isOptional || layerB.layerConfig?.isOptional) {
    const lsa = getLayerGeometryType(layerA);
    const lsb = getLayerGeometryType(layerB);
    // If geometry types are the same, layerA should be above (at the beginning)
    if (lsa === 0) return false;
    // If layerB is polygon, layerA should be above
    if (lsb === 0) return true;
    // Otherwise, higher index means above
    return lsa > lsb;
  }

  // First check if we have an active module to prioritize
  if (activeModule && config.modules[activeModule]?.layers) {
    return sortActiveModuleLayers({
      config,
      activeModule,
      layerA,
      layerB,
    });
  }

  //TODO: look at this code, it can be optimized
  if (config.activeModules) {
    let ret = null;
    config.activeModules.forEach((module) => {
      if (ret !== null) return;
      config.modules[module].layers.forEach((layerId) => {
        if (ret !== null) return;

        if (
          layerA.layerConfig.id === layerId ||
          layerA.layerConfig.alias === layerId
        )
          ret = false;
        if (
          layerB.layerConfig.id === layerId ||
          layerB.layerConfig.alias === layerId
        )
          ret = true;
      });
    });
    return ret;
  }

  if (config.layers) {
    let ret = null;
    config.layers.forEach((layerId) => {
      if (ret !== null) return;

      if (
        layerA.layerConfig.id === layerId ||
        layerA.layerConfig.alias === layerId
      )
        ret = false;
      if (
        layerB.layerConfig.id === layerId ||
        layerB.layerConfig.alias === layerId
      )
        ret = true;
    });
    return ret;
  }

  const lsa = getLayerGeometryType(layerA);
  const lsb = getLayerGeometryType(layerB);
  if (lsa !== lsb) return lsa > lsb;

  return layerA.layerIndex > layerB.layerIndex;
};

const getLayerGeometryType = (layer) => {
  if (layer.geometryType) return layerSequence.indexOf(layer.geometryType);
};

const getColoredLabelClass = (layer, symbol) => {
  const colorDefinitions = layer.renderer?.getColorDefinitions();
  const placement = "above-center";
  symbol.haloColor = "white"; // #949494 gray
  symbol.haloSize = 2;
  symbol.yoffset = -5;
  symbol.font.size = 9;
  symbol.font.family = "Roboto";
  symbol.font.style = "italic";
  const labelClasses = [];
  colorDefinitions.forEach((colorDef) => {
    const field = colorDef.field[0];
    labelClasses.push({
      labelExpressionInfo: {
        expression: `var values = Split($feature.${layer.layerConfig.labelField}, ' ');
                              var ret = ''; var temp = '';
                              for(var i in values){
                                //temp += values[i] + ' ';
                                var newTemp = temp + values[i]
                                if (Count(newTemp) > 20){
                                  ret += temp + TextFormatting.NewLine + values[i] + ' ';
                                  temp = '';
                                } else {
                                  temp += values[i] + ' ';
                                }
                              }
                              ret += temp;
                              return ret;`,
      },
      allowOverrun: false,
      symbol: {
        ...symbol,
        color: colorDef.color, //'#d68a00'
      },
      useCodedValues: true,
      repeatLabel: false,
      labelPlacement: placement,
      maxScale: 0,
      minScale: 25000000,
      where: `${field.name} = ${colorDef.value}`,
    });
  });
  return labelClasses;
};

const addLabelingInfo = (layer) => {
  if (!layer.layerConfig || !layer.layerConfig.labelField) return;

  const symbol = {
    type: "text",
    color: "#242424",
    font: { size: 8 },
  };

  if (
    layer.geometryType === "polyline" ||
    layer.geometryType === "line" ||
    layer.geometryType === "polygon"
  ) {
    const placement =
      layer.geometryType === "polyline" || layer.geometryType === "line"
        ? "center-along"
        : layer.geometryType === "polygon"
        ? "always-horizontal"
        : "below-center";

    symbol.haloColor = "white";
    symbol.haloSize = 2;
    symbol.yoffset = 15;

    if (!layer.layerConfig.labelField.includes("$feature")) {
      layer.labelingInfo = getColoredLabelClass(layer, symbol);
    } else {
      layer.labelingInfo = [
        {
          labelExpressionInfo: {
            expression: `${layer.layerConfig.labelField}`,
          },
          symbol: symbol,
          useCodedValues: true,
          repeatLabel: false,
          labelPlacement: placement,
        },
      ];
    }
  } else {
    const captionFieldName = layer.layerConfig?.labelField;
    const captionField = layer.fields.find((f) => f.name === captionFieldName);

    if (captionFieldName && !captionField)
      console.warn(
        "Nonexistent captionField specified: '" +
          captionFieldName +
          "' Layer: " +
          layer.title
      );

    if (captionField) {
      layer.labelingInfo = getColoredLabelClass(layer, symbol);
    } else {
      symbol.backgroundColor = [255, 255, 255, 1];
      symbol.borderLineColor = "black";
      symbol.borderLineSize = 0.5;

      const labelExpressionInfo = {
        expression: `${layer.layerConfig.labelField}`,
      };
      layer.labelingInfo = [
        {
          labelExpressionInfo: labelExpressionInfo,
          labelPlacement: "below-center",
          symbol: symbol,
          allowOverrun: false,
        },
      ];
    }
  }
};

/**
 * Add the filterFields element to the layerConfig layer attribute
 * We can spare time/network if a polyline layer is loaded with the default filter value
 */
export const initFilters = (layer, config, activeModule) => {
  if (layer.type !== "feature" || !layer.fields) return;

  if (config.defaultFilters[activeModule])
    applyCurrentFiltersOnLayer(
      layer,
      config.defaultFilters[activeModule],
      config
    );
};

/**
 * Offline functionality: Query features of the layer in advance. this way we can cache all features and the offline
 * version will not try to load them when a popup is to be opened
 *
 * Warning: This code is not mature enough to be used in production
 */
const prepareLayerForOfflineUse = (l) => {
  if (
    l.title === "glcconops_view" &&
    l.type === "feature" &&
    l.capabilities.query.supportsCacheHint
  ) {
    const fieldNames = l.fields.map((f) => f.name).sort();
    l.queryObjectIds().then((objectIds) => {
      if (objectIds.length > 200) objectIds = objectIds.slice(0, 200);

      let q = l.createQuery();
      q.objectIds = objectIds;
      q.outFields = fieldNames;
      q.returnM = true;
      q.returnZ = true;
      q.outSpatialReference = 102100;
      q.where = l.definitionExpression || "1=1";
      l.queryFeatures(q);

      //if (l.capabilities.operations.supportsQueryAttachments) {
      //layer.queryAttachments({objectIds: objectIds})
      //}
    });
  }
};
